import ProfileService from 'services/ProfileService'
import StorageService from 'services/StorageService'

import config from 'config'
import { posthogClient } from 'plugins/posthog'
import sentry from 'sentry'

import dayjs from 'utils/dayjs'
import calculateRange from 'helpers/calculateRange'
import adjustSegmentBy from 'helpers/adjustSegmentBy'
import { parseSummary, calculateCurrentSummary } from 'utils/activity'
import { toast } from 'utils/toast'
import debounce from 'lodash/debounce'

const PROFILE_STORAGE_KEY = 'profile'

const dateRangeOptions = calculateRange()
const defaultDateRange = dateRangeOptions[3]

// Debounced persist function to reduce IndexedDB writes
const debouncedPersist = debounce(async (state) => {
  try {
    // Try to save full state first
    const fullPersisted = {
      segmentBy: state.segmentBy,
      dateRange: state.dateRange,
      summary: state.summary,
      activities: state.activities,
      allActivities: state.allActivities,
      fitness: state.fitness,
      currentSummary: state.currentSummary,
      dashboardDateRange: state.dashboardDateRange,
      dashboardCurrentSummary: state.dashboardCurrentSummary,
      athleteProfile: state.athleteProfile,
      athleteProfileAllTime: state.athleteProfileAllTime,
      trainingDistribution: state.trainingDistribution,
      trainingReadiness: state.trainingReadiness,
      // Persisted alongside trainingReadiness: the Recovery tile reads both,
      // so restoring only one makes the card render a transitional state.
      healthMetrics: state.healthMetrics,
      refreshedAt: state.refreshedAt
    }

    await StorageService.persist(PROFILE_STORAGE_KEY, fullPersisted)
  } catch (e) {
    console.warn('Full persist failed, trying minimal fallback:', e)

    // Fallback to essential data only
    try {
      const minimalPersisted = {
        segmentBy: state.segmentBy,
        dateRange: state.dateRange,
        dashboardDateRange: state.dashboardDateRange,
        summary: state.summary
      }

      await StorageService.persist(PROFILE_STORAGE_KEY, minimalPersisted)
    } catch (fallbackError) {
      console.warn('Minimal persist also failed:', fallbackError)
    }
  }
}, 2000) // Wait 2 seconds before persisting

const types = {
  APP_INITIALIZED: 'APP_INITIALIZED',
  APP_LOADING_SET: 'APP_LOADING_SET',
  USER_SET: 'USER_SET',
  SUMMARY_SET: 'SUMMARY_SET',
  ACTIVITIES_SET: 'ACTIVITIES_SET',
  ALL_ACTIVITIES_SET: 'ALL_ACTIVITIES_SET',
  FITNESS_SET: 'FITNESS_SET',
  DATE_RANGE_SET: 'DATE_RANGE_SET',
  CURRENT_SUMMARY_SET: 'CURRENT_SUMMARY_SET',
  DASHBOARD_DATE_RANGE_SET: 'DASHBOARD_DATE_RANGE_SET',
  DASHBOARD_CURRENT_SUMMARY_SET: 'DASHBOARD_CURRENT_SUMMARY_SET',
  SEGMENT_BY_SET: 'SEGMENT_BY_SET',
  ACHIEVEMENTS_SET: 'ACHIEVEMENTS_SET',
  ATHLETE_PROFILE_SET: 'ATHLETE_PROFILE_SET',
  ATHLETE_PROFILE_ALL_TIME_SET: 'ATHLETE_PROFILE_ALL_TIME_SET',
  TRAINING_DISTRIBUTION_SET: 'TRAINING_DISTRIBUTION_SET',
  AEROBIC_BASE_TREND_SET: 'AEROBIC_BASE_TREND_SET',
  TRAINING_READINESS_SET: 'TRAINING_READINESS_SET',
  DAILY_SUGGESTION_SET: 'DAILY_SUGGESTION_SET',
  HEALTH_METRICS_SET: 'HEALTH_METRICS_SET',
  FIRST_ACTIVITY_DATE_SET: 'FIRST_ACTIVITY_DATE_SET',
  REFRESHED_AT_SET: 'REFRESHED_AT_SET'
}

const defaultState = () => ({
  user: config.user,
  summary: undefined,
  activities: undefined,
  allActivities: undefined,
  fitness: undefined,
  achievements: undefined,
  athleteProfile: undefined,
  athleteProfileAllTime: undefined,
  trainingDistribution: undefined,
  aerobicBaseTrend: undefined,
  trainingReadiness: undefined,
  dailySuggestion: undefined,
  healthMetrics: undefined,
  currentSummary: undefined,
  dateRange: defaultDateRange,
  dashboardCurrentSummary: undefined,
  dashboardDateRange: defaultDateRange,
  segmentBy: 'week',
  dateRangeOptions: dateRangeOptions,
  segmentByOptions: ['day', 'week', 'month', 'year'],
  refreshedAt: undefined
})

const persistedState = async () => {
  const data = await StorageService.load(PROFILE_STORAGE_KEY, {})

  // Recalculate selected date range option
  if (data?.dateRange) {
    data.dateRange = dateRangeOptions.find(({ id }) => id === data.dateRange.id)
  }

  if (data?.dashboardDateRange) {
    data.dashboardDateRange = dateRangeOptions.find(({ id }) => id === data.dashboardDateRange.id)
  } else if (data?.dateRange) {
    data.dashboardDateRange = dateRangeOptions.find(({ id }) => id === data.dateRange.id)
  }

  if (data && data.refreshedAt) {
    try {
      const now = new Date()
      const lastRefresh = new Date(data.refreshedAt)
      // hours since last refresh
      const hoursSinceLastRefresh = (now - lastRefresh) / 1000 / 60 / 60
      if (hoursSinceLastRefresh > 1) {
        // Remove all keys except dateRange, dashboardDateRange, segmentBy, refreshedAt
        Object.keys(data).forEach((key) => {
          if (!['dateRange', 'dashboardDateRange', 'segmentBy', 'refreshedAt'].includes(key)) {
            delete data[key]
          }
        })
      }
    } catch (e) {
      Object.keys(data).forEach((key) => {
        if (!['dateRange', 'dashboardDateRange', 'segmentBy', 'refreshedAt'].includes(key)) {
          delete data[key]
        }
      })
    }
  }

  return data
}

// Initialize state with defaults
const state = {
  ...defaultState(),
  isLoading: false,
  isInitialized: false,
  errors: {}
}

const actions = {
  async loadPersistedState({ commit }) {
    try {
      const persistedData = await persistedState()
      if (persistedData && Object.keys(persistedData).length > 0) {
        // Use mutations to properly trigger reactivity
        if (persistedData.summary) commit(types.SUMMARY_SET, { summary: persistedData.summary })
        if (persistedData.activities) commit(types.ACTIVITIES_SET, { activities: persistedData.activities })
        if (persistedData.allActivities) commit(types.ALL_ACTIVITIES_SET, { allActivities: persistedData.allActivities })
        if (persistedData.fitness) commit(types.FITNESS_SET, { fitness: persistedData.fitness })
        if (persistedData.currentSummary) commit(types.CURRENT_SUMMARY_SET, { currentSummary: persistedData.currentSummary })
        if (persistedData.dashboardCurrentSummary) {
          commit(types.DASHBOARD_CURRENT_SUMMARY_SET, { dashboardCurrentSummary: persistedData.dashboardCurrentSummary })
        }
        if (persistedData.dateRange) commit(types.DATE_RANGE_SET, { dateRange: persistedData.dateRange })
        if (persistedData.dashboardDateRange) commit(types.DASHBOARD_DATE_RANGE_SET, { dashboardDateRange: persistedData.dashboardDateRange })
        if (persistedData.segmentBy) commit(types.SEGMENT_BY_SET, persistedData.segmentBy)
        if (persistedData.trainingReadiness) commit(types.TRAINING_READINESS_SET, { trainingReadiness: persistedData.trainingReadiness })
        if (persistedData.healthMetrics) commit(types.HEALTH_METRICS_SET, { healthMetrics: persistedData.healthMetrics })
        if (persistedData.refreshedAt) commit(types.REFRESHED_AT_SET, persistedData.refreshedAt)
      }
    } catch (e) {
      // Silently fail if we can't load persisted state
      console.warn('Failed to load persisted state:', e)
      sentry.captureException(e)
    }
  },

  async init({ commit }) {
    try {
      const user = await ProfileService.getUser()

      // Fill the athlete's timezone from the device ONLY when it's not set yet
      // (server-side "today" anchors depend on it). Set-once by design: privacy
      // browsers spoof the zone (Firefox RFP/Tor report UTC) and multi-device
      // users would otherwise flip the value — and recompute their profile
      // caches — on every visit. Later changes go through Settings.
      try {
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (browserTimezone && !user.timezone) {
          user.timezone = browserTimezone
          ProfileService.saveTimezone(browserTimezone).catch(() => {})
        }
      } catch (e) {
        // Intl unavailable — keep the server value
      }

      commit(types.USER_SET, { user })

      try {
        setTimeout(() => {
          posthogClient()?.identify(user.id)
          sentry.setUser({ id: user.id })
        }, 0)
      } catch (e) {
        // Ignore errors related to posthog
      }
    } catch (e) {
      if (e?.response?.status != 401) {
        sentry.captureException(e)
      }
    }

    commit(types.APP_INITIALIZED)
  },

  async refreshCurrentSummary({ commit, state }) {
    const currentSummary = calculateCurrentSummary(state.summary, state.dateRange.from, state.dateRange.to)
    commit(types.CURRENT_SUMMARY_SET, { currentSummary })

    const dashboardCurrentSummary = calculateCurrentSummary(state.summary, state.dashboardDateRange.from, state.dashboardDateRange.to)
    commit(types.DASHBOARD_CURRENT_SUMMARY_SET, { dashboardCurrentSummary })

    // Use debounced persist to avoid blocking UI
    debouncedPersist(state)
  },

  async loadProfile({ commit, dispatch, state }, { useCache = false } = {}) {
    // If useCache is true, preload cached data first
    if (useCache) {
      commit(types.APP_LOADING_SET, true)
      await dispatch('loadPersistedState')
      commit(types.APP_LOADING_SET, false)
    }

    // Only show loading if we have no activities at all (not even cached)
    if (!state.activities) {
      commit(types.APP_LOADING_SET, true)
    }

    try {
      // Load both API calls in parallel for better performance
      const [activities, summaryData] = await Promise.all([ProfileService.loadActivities(), ProfileService.loadSummary()])

      const {
        summary,
        fitness,
        keys,
        athlete_profile,
        athlete_profile_all_time,
        training_distribution,
        aerobic_base_trend,
        training_readiness,
        daily_suggestion,
        cp_analysis,
        health_metrics
      } = summaryData

      // TODO: Remove later
      const parsedSummary = parseSummary(summary, keys)
      commit(types.SUMMARY_SET, { summary: parsedSummary })

      commit(types.ALL_ACTIVITIES_SET, { allActivities: activities })
      commit(types.ACTIVITIES_SET, { activities: [...activities].filter((a) => a.is_cycling) })

      commit(types.FITNESS_SET, { fitness })

      // Set athlete profile data if available
      if (athlete_profile) {
        commit(types.ATHLETE_PROFILE_SET, { athleteProfile: athlete_profile })
      }
      if (athlete_profile_all_time) {
        commit(types.ATHLETE_PROFILE_ALL_TIME_SET, { athleteProfileAllTime: athlete_profile_all_time })
      }

      if (training_distribution) {
        commit(types.TRAINING_DISTRIBUTION_SET, { trainingDistribution: training_distribution })
      }

      if (aerobic_base_trend) {
        commit(types.AEROBIC_BASE_TREND_SET, { aerobicBaseTrend: aerobic_base_trend })
      }

      if (training_readiness) {
        commit(types.TRAINING_READINESS_SET, { trainingReadiness: training_readiness })
      }

      if (daily_suggestion) {
        commit(types.DAILY_SUGGESTION_SET, { dailySuggestion: daily_suggestion })
      }

      // Always commit (not just when present) so disconnecting Garmin or
      // leaving the preview gate clears stale recovery data on next load.
      commit(types.HEALTH_METRICS_SET, { healthMetrics: health_metrics })

      if (cp_analysis) {
        commit(types.USER_SET, { user: { ...state.user, cp_analysis } })
      }

      commit(types.REFRESHED_AT_SET, new Date())

      if (activities.length) {
        try {
          const firstActivityDate = dayjs(activities[activities.length - 1].start_date_local).startOf('day')
          commit(types.FIRST_ACTIVITY_DATE_SET, firstActivityDate)
        } catch (e) {
          sentry.captureException(e)
        }
      }

      await dispatch('refreshCurrentSummary')
    } catch (e) {
      sentry.captureException(e)
    }

    commit(types.APP_LOADING_SET, false)
  },

  async loadAchievements({ commit, dispatch }) {
    dispatch('setAppLoading', true)

    const result = await ProfileService.getAchievements()

    commit(
      types.ACHIEVEMENTS_SET,
      result.sort((a, b) => new Date(b.date) - new Date(a.date))
    )

    dispatch('setAppLoading', false)
  },

  async loadAthleteProfile({ commit, dispatch }) {
    // Check if athlete profile data is already available
    if (this.getters['profile/athleteProfile'] && this.getters['profile/athleteProfileAllTime']) {
      return
    }

    dispatch('setAppLoading', true)

    try {
      const { athlete_profile, athlete_profile_all_time } = await ProfileService.loadSummary()

      if (athlete_profile) {
        commit(types.ATHLETE_PROFILE_SET, { athleteProfile: athlete_profile })
      }
      if (athlete_profile_all_time) {
        commit(types.ATHLETE_PROFILE_ALL_TIME_SET, { athleteProfileAllTime: athlete_profile_all_time })
      }
    } catch (e) {
      sentry.captureException(e)
    }

    dispatch('setAppLoading', false)
  },

  setDateRange({ commit, dispatch, state }, { dateRange, type }) {
    if (type === 'dashboard') {
      commit(types.DASHBOARD_DATE_RANGE_SET, { dashboardDateRange: dateRange })
      dispatch('refreshCurrentSummary')
    } else {
      commit(types.DATE_RANGE_SET, { dateRange })

      const adjustedSegmentBy = adjustSegmentBy(dateRange.id, state.segmentBy)
      if (adjustedSegmentBy) {
        dispatch('setSegmentBy', adjustedSegmentBy)
      } else {
        dispatch('refreshCurrentSummary')
      }
    }
  },

  setSegmentBy({ commit, dispatch }, segmentBy) {
    commit(types.SEGMENT_BY_SET, segmentBy)
    dispatch('refreshCurrentSummary')
  },

  setAppLoading({ commit }, isLoading) {
    commit(types.APP_LOADING_SET, isLoading)
  },

  async acceptFtpSuggestion({ commit, dispatch }, suggestion) {
    try {
      const { user } = await ProfileService.acceptFtpSuggestion({ activity_record_id: suggestion.activity_record_id })
      commit(types.USER_SET, { user })
      dispatch('preferences/refreshFromUser', user, { root: true })
      posthogClient()?.capture('ftp_suggestion_accepted', { ftp: suggestion.value })
      toast(`FTP updated to ${suggestion.value}W. Power zones recalculated.`)
    } catch (e) {
      toast("Couldn't update your FTP. Please try again.", { type: 'error' })
      sentry.captureException(e)
    }
  },

  async dismissFtpSuggestion({ commit }, suggestion) {
    try {
      const { user } = await ProfileService.dismissFtpSuggestion({ activity_record_id: suggestion.activity_record_id })
      commit(types.USER_SET, { user })
      posthogClient()?.capture('ftp_suggestion_dismissed', { ftp: suggestion.value })
    } catch (e) {
      toast("Couldn't dismiss the suggestion. Please try again.", { type: 'error' })
      sentry.captureException(e)
    }
  },

  async signOut({ commit }) {
    try {
      const result = await ProfileService.signOut()
      commit(types.USER_SET, { user: undefined })
      await StorageService.remove(PROFILE_STORAGE_KEY)
      posthogClient()?.reset()

      return result
    } catch (e) {
      sentry.captureException(e)
    }
  }
}

const mutations = {
  [types.APP_INITIALIZED](state) {
    state.isInitialized = true
  },

  [types.APP_LOADING_SET](state, isLoading) {
    state.isLoading = isLoading
  },

  [types.USER_SET](state, { user }) {
    state.user = user
  },

  [types.SUMMARY_SET](state, { summary }) {
    state.summary = summary
  },

  [types.CURRENT_SUMMARY_SET](state, { currentSummary }) {
    state.currentSummary = currentSummary
  },

  [types.ACTIVITIES_SET](state, { activities }) {
    try {
      if (state.activities && activities) {
        const currentSignature = `${state.activities.length}-${state.activities[0]?.id}-${state.activities[0]?.updated_at}-${
          state.activities[state.activities.length - 1]?.id
        }`
        const newSignature = `${activities.length}-${activities[0]?.id}-${activities[0]?.updated_at}-${activities[activities.length - 1]?.id}`
        if (currentSignature === newSignature) {
          return
        }
      }
    } catch (e) {
      // Fallback to always update if comparison fails
    }
    // Use Object.freeze to prevent Vue from making nested objects reactive
    state.activities = Object.freeze(activities)
  },

  [types.ALL_ACTIVITIES_SET](state, { allActivities }) {
    // Use Object.freeze to prevent Vue from making nested objects reactive
    state.allActivities = Object.freeze(allActivities)
  },

  [types.FITNESS_SET](state, { fitness }) {
    try {
      if (state.fitness && fitness) {
        // Include CTL values in signature to detect different users with similar date ranges
        const currentSignature = `${state.fitness.length}-${state.fitness[0]?.date}-${state.fitness[0]?.ctl}-${state.fitness[state.fitness.length - 1]?.date}-${
          state.fitness[state.fitness.length - 1]?.ctl
        }`
        const newSignature = `${fitness.length}-${fitness[0]?.date}-${fitness[0]?.ctl}-${fitness[fitness.length - 1]?.date}-${fitness[fitness.length - 1]?.ctl}`
        if (currentSignature === newSignature) {
          return
        }
      }
    } catch (e) {
      // Fallback to always update if comparison fails
    }
    state.fitness = fitness
  },

  [types.DATE_RANGE_SET](state, { dateRange }) {
    state.dateRange = dateRange
  },

  [types.DASHBOARD_DATE_RANGE_SET](state, { dashboardDateRange }) {
    state.dashboardDateRange = dashboardDateRange
  },

  [types.DASHBOARD_CURRENT_SUMMARY_SET](state, { dashboardCurrentSummary }) {
    // Always update: the summary object shape is stable (year/month/week/day), so
    // a keys-only comparison can incorrectly prevent refresh when values change.
    //
    // Freeze to avoid Vue observing a large nested object (perf), while still
    // allowing reactive updates on assignment.
    state.dashboardCurrentSummary = dashboardCurrentSummary ? Object.freeze(dashboardCurrentSummary) : dashboardCurrentSummary
  },

  [types.SEGMENT_BY_SET](state, segmentBy) {
    state.segmentBy = segmentBy
  },

  [types.ACHIEVEMENTS_SET](state, achievements) {
    state.achievements = achievements
  },

  [types.ATHLETE_PROFILE_SET](state, { athleteProfile }) {
    try {
      if (state.athleteProfile && athleteProfile) {
        const currentSignature = `${state.athleteProfile.rider_type?.name}-${Object.keys(state.athleteProfile).length}`
        const newSignature = `${athleteProfile.rider_type?.name}-${Object.keys(athleteProfile).length}`
        if (currentSignature === newSignature) {
          return
        }
      }
    } catch (e) {
      // Fallback to always update if comparison fails
    }
    state.athleteProfile = athleteProfile
  },

  [types.ATHLETE_PROFILE_ALL_TIME_SET](state, { athleteProfileAllTime }) {
    state.athleteProfileAllTime = athleteProfileAllTime
  },

  [types.TRAINING_DISTRIBUTION_SET](state, { trainingDistribution }) {
    state.trainingDistribution = trainingDistribution ? Object.freeze(trainingDistribution) : trainingDistribution
  },

  [types.AEROBIC_BASE_TREND_SET](state, { aerobicBaseTrend }) {
    state.aerobicBaseTrend = aerobicBaseTrend ? Object.freeze(aerobicBaseTrend) : aerobicBaseTrend
  },

  [types.TRAINING_READINESS_SET](state, { trainingReadiness }) {
    state.trainingReadiness = trainingReadiness ? Object.freeze(trainingReadiness) : trainingReadiness
  },

  [types.DAILY_SUGGESTION_SET](state, { dailySuggestion }) {
    state.dailySuggestion = dailySuggestion ? Object.freeze(dailySuggestion) : dailySuggestion
  },

  [types.HEALTH_METRICS_SET](state, { healthMetrics }) {
    state.healthMetrics = healthMetrics ? Object.freeze(healthMetrics) : healthMetrics
  },

  [types.FIRST_ACTIVITY_DATE_SET](state, date) {
    state.dateRangeOptions[state.dateRangeOptions.length - 1].from = date
  },

  [types.REFRESHED_AT_SET](state, date) {
    state.refreshedAt = date
  }
}

const getters = {
  isInitialized(state) {
    return state.isInitialized
  },

  isLoading(state) {
    return state.isLoading
  },

  user(state) {
    return state.user
  },

  userWeight(state) {
    return state.user.weight
  },

  userWeightMetric(state) {
    return state.user.weight_metric
  },

  userFtp(state) {
    return state.user.ftp
  },

  userWKg(_, getters) {
    return getters['userFtp'] / getters['userWeightMetric']
  },

  activities(state) {
    return state.activities
  },

  allActivities(state) {
    return state.allActivities
  },

  summary(state) {
    return state.summary
  },

  fitness(state) {
    return state.fitness
  },

  achievements(state) {
    return state.achievements
  },

  athleteProfile(state) {
    return state.athleteProfile
  },

  athleteProfileAllTime(state) {
    return state.athleteProfileAllTime
  },

  trainingDistribution(state) {
    return state.trainingDistribution
  },

  aerobicBaseTrend(state) {
    return state.aerobicBaseTrend
  },

  trainingReadiness(state) {
    return state.trainingReadiness
  },

  healthMetrics(state) {
    return state.healthMetrics
  },

  dailySuggestion(state) {
    return state.dailySuggestion
  },

  currentSummary(state) {
    return state.currentSummary
  },

  dateRange(state) {
    return state.dateRange
  },

  dashboardCurrentSummary(state) {
    return state.dashboardCurrentSummary
  },

  dashboardDateRange(state) {
    return state.dashboardDateRange
  },

  segmentBy(state) {
    return state.segmentBy
  },

  dateRangeOptions(state) {
    return state.dateRangeOptions
  },

  segmentByOptions(state) {
    return state.segmentByOptions
  },

  errors(state) {
    return state.errors
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
